import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Info } from "lucide-react";
import { api, listDiscountRules, getBrandOptions } from "@/services/api";
import type { BrandOption } from "@/services/api";
import { derivedOptions } from "@/lib/filterOptions";
import { BrandLockTable, isRuleExpired } from "@/components/BrandLockTable";
import { BrandLockDetailPanel } from "@/components/BrandLockDetailPanel";
import { OemDiscountsTable, statusOf as ruleStatusOf } from "@/components/OemDiscountsTable";
import { OemDiscountRulePanel } from "@/components/OemDiscountRulePanel";
import {
  ReferenceLoading, ReferenceError, ReferenceEmpty, type LoadStatus,
} from "@/components/ReferenceDataStates";
import { useCanEditSettings } from "@/auth/AuthContext";
import type { BrandLockRule, OemDiscountRule } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke } from "@/styles/tokens";

type Tab = "brand-locks" | "discounts";

// ⚠️ CATEGORY, OEM AND DEAL TYPE ARE DERIVED FROM THE LOADED ROWS. They were
// literal arrays, and OEM_OPTS in particular omitted "Poly" -- the same
// omission that made the only real product in /catalog unfilterable before
// that screen started deriving its options. See lib/filterOptions.ts.
//
// STATUS STAYS A LITERAL, deliberately: it is the range of isRuleExpired /
// isDiscountExpired -- a verdict computed from the dates and `active` -- not
// a column any row holds. Deriving it would drop "Expired" from the filter on
// a day when nothing happened to be expired, which is when somebody looks.
const STATUS_OPTS   = ["All", "Active", "Expired"];

// Brand locks have no backend model or authoritative source in this build.
// The production rule provider is therefore empty and nothing is enforced.
// This must not say "open sourcing allowed": absence of configuration is not
// commercial approval. Discounts are separate and remain Odoo-backed.
const BRAND_LOCK_NOTICE =
  "Brand locks are not configured in this deployment. No authoritative " +
  "brand-lock rules are loaded or enforced.";

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};

// ⚠️ ONE STYLE, TWO RENDER SITES, AND THE SECOND SITE IS THE WHOLE POINT.
// "Add Discount" used to live only inside the discounts filter bar, which
// renders on `totalCount > 0`. On a database with no rules yet -- which is
// every database until an Admin creates the first one -- the empty state was
// all that rendered, so the only way to create a rule was gated on a rule
// already existing. An Admin read "No discount rules yet. Add one for a
// customer and OEM." with nothing to press.
//
// The filter bar's own gate is CORRECT and stays: a filter bar over an empty
// grid reads as "nothing matched" rather than "there is nothing here". A
// create action is not a filter, so it does not belong behind that gate.
//
// The style is shared so the two buttons cannot drift apart; the `canEdit`
// gate is deliberately NOT folded in with it. It stays written out at each
// render site, where it is visible to a reader and to deadControls.test.tsx,
// which pins BOTH create testids -- discounts-add-discount and
// discounts-add-first-discount -- as Admin-gated at every occurrence.
const ADD_DISCOUNT_BUTTON_STYLE: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  backgroundColor: colors.indigo600, color: semanticColors.onAccent,
  border: "none", borderRadius: radius.base,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
  cursor: "pointer",
};

export default function PricingPage() {
  const [tab, setTab] = useState<Tab>("brand-locks");
  const rules = useMemo(() => api.getBrandLockRules(), []);
  const canEditRules = useCanEditSettings();

  // Brand Locks state
  const [blSearch, setBlSearch]     = useState("");
  const [blCategory, setBlCategory] = useState<string>("All");
  const [blStatus, setBlStatus]     = useState<string>("All");
  const [selectedRule, setSelectedRule] = useState<BrandLockRule | null>(null);

  // ⚠️ DISCOUNTS COME OUT OF THE DATABASE. api.getDiscountRules() is gone.
  // mocks/discountRules.ts is QUARANTINED as of D3a-iii: nothing renders it and
  // nothing prices from it -- an invented rate on screen under the same heading
  // as a real one is the two-catalogs-live bug applied to money, and an
  // invented rate reaching a PRICE was the bug D3a-iii removed.
  const [discounts, setDiscounts] = useState<OemDiscountRule[]>([]);
  const [dcLoad, setDcLoad] = useState<LoadStatus>("loading");
  const [dcError, setDcError] = useState("");
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([]);

  const [dcSearch, setDcSearch] = useState("");
  const [dcOem, setDcOem]       = useState<string>("All");
  const [dcStatus, setDcStatus] = useState<string>("All");
  const [selectedDiscount, setSelectedDiscount] = useState<OemDiscountRule | null>(null);
  // app review P2-5: a saved rule says so, and the panel closes.
  const [discountSavedText, setDiscountSavedText] = useState("");
  // Distinct from `selectedDiscount === null`, which also means "nothing
  // selected". Creating is a deliberate act, so it gets its own flag.
  const [creatingRule, setCreatingRule] = useState(false);

  const loadDiscounts = useCallback(async () => {
    setDcLoad("loading");
    setDcError("");
    const result = await listDiscountRules();
    if (!result.ok) {
      // Cleared, never left stale and NEVER replaced by the mock. These are
      // rates somebody may be about to quote off.
      setDiscounts([]);
      setSelectedDiscount(null);
      setDcError(result.error ?? "Backend reported an unknown failure.");
      setDcLoad("error");
      return;
    }
    setDiscounts(result.rules ?? []);
    setDcLoad("ok");
  }, []);

  useEffect(() => { loadDiscounts(); }, [loadDiscounts]);

  // The brands a rule may name, WITH IDS -- a write addresses the record by
  // id. Only an Admin can write, so only an Admin needs them.
  useEffect(() => {
    if (!canEditRules) return;
    let cancelled = false;
    (async () => {
      const result = await getBrandOptions();
      if (cancelled || !result.ok) return;
      setBrandOptions(result.brands ?? []);
    })();
    return () => { cancelled = true; };
  }, [canEditRules]);

  const blCategoryOptions = useMemo(
    () => derivedOptions(rules, (r) => r.category), [rules],
  );
  // ⚠️ DERIVED FROM THE LOADED ROWS, like every other filter in this app. The
  // OEM list was a literal that omitted "Poly" -- the same omission that made
  // the only real product in /catalog unfilterable.
  const dcOemOptions = useMemo(
    () => derivedOptions(discounts, (d) => d.brandName), [discounts],
  );

  useEffect(() => {
    if (dcOem !== "All" && !dcOemOptions.includes(dcOem)) setDcOem("All");
  }, [dcOemOptions, dcOem]);

  const filteredRules = useMemo(() => {
    const q = blSearch.trim().toLowerCase();
    return rules.filter((r) => {
      if (blCategory !== "All" && r.category !== blCategory) return false;
      const expired = isRuleExpired(r);
      if (blStatus === "Active"  && expired)  return false;
      if (blStatus === "Expired" && !expired) return false;
      if (q.length > 0 && !r.endClient.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rules, blSearch, blCategory, blStatus]);

  const filteredDiscounts = useMemo(() => {
    const q = dcSearch.trim().toLowerCase();
    return discounts.filter((d) => {
      if (dcOem !== "All" && d.brandName !== dcOem) return false;
      if (dcStatus !== "All" && ruleStatusOf(d) !== dcStatus) return false;
      if (q.length > 0) {
        const hay = `${d.partnerName ?? ""} ${d.brandName ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [discounts, dcSearch, dcOem, dcStatus]);

  return (
    <div
      data-testid="pricing-page"
      className="flex"
      style={{ height: "calc(100vh - 48px)" }}
    >
      <div className="flex flex-col" style={{ padding: spacing[24], flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: spacing[16] }}>
          <div
            data-testid="pricing-title"
            style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight }}
          >
            Pricing Rules
          </div>
          <div
            data-testid="pricing-subtitle"
            style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}
          >
            Client brand locks and negotiated discounts
          </div>
        </div>

        <div
          data-testid="pricing-tabs"
          className="flex items-center"
          style={{ borderBottom: `1px solid ${semanticColors.border}`, marginBottom: spacing[16], gap: spacing[24] }}
        >
          {(["brand-locks", "discounts"] as Tab[]).map((t) => {
            const active = tab === t;
            const label = t === "brand-locks" ? "Brand Locks" : "Discounts";
            return (
              <button
                key={t}
                data-testid={`tab-${t}`}
                data-active={active ? "true" : "false"}
                onClick={() => setTab(t)}
                style={{
                  background: "transparent", border: "none",
                  padding: `${spacing[10]}px ${spacing[2]}px`, cursor: "pointer",
                  fontSize: typography.fontSize.base,
                  color: active ? semanticColors.textPrimary : semanticColors.textMuted,
                  fontWeight: active ? typography.fontWeight.medium : typography.fontWeight.regular,
                  borderBottom: `2px solid ${active ? colors.indigo600 : "transparent"}`,
                  marginBottom: "-1px",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {tab === "discounts" && discountSavedText !== "" && (
          <div data-testid="discounts-saved" role="status" style={{
            marginBottom: spacing[12], padding: `${spacing[6]}px ${spacing[12]}px`,
            backgroundColor: colors.green50, border: `1px solid ${colors.green100}`,
            color: colors.green600, borderRadius: radius.base, fontSize: typography.fontSize.sm,
          }}>
            {discountSavedText}
          </div>
        )}
        {tab === "brand-locks" ? (
          <BrandLocksTab
            rules={filteredRules}
            totalCount={rules.length}
            categoryOptions={blCategoryOptions}
            search={blSearch} onSearch={setBlSearch}
            category={blCategory} onCategory={setBlCategory}
            status={blStatus} onStatus={setBlStatus}
            selectedId={selectedRule?.id}
            onRowClick={setSelectedRule}
          />
        ) : (
          <DiscountsTab
            rows={filteredDiscounts}
            totalCount={discounts.length}
            loadStatus={dcLoad}
            loadError={dcError}
            onRetry={loadDiscounts}
            canEdit={canEditRules}
            onNew={() => { setSelectedDiscount(null); setCreatingRule(true); }}
            oemOptions={dcOemOptions}
            search={dcSearch} onSearch={setDcSearch}
            oem={dcOem} onOem={setDcOem}
            status={dcStatus} onStatus={setDcStatus}
            selectedId={selectedDiscount?.id}
            onRowClick={(r) => { setCreatingRule(false); setSelectedDiscount(r); }}
          />
        )}
      </div>

      {tab === "brand-locks" && selectedRule && (
        <BrandLockDetailPanel
          rule={selectedRule}
          onClose={() => setSelectedRule(null)}
        />
      )}
      {tab === "discounts" && (selectedDiscount || creatingRule) && (
        <OemDiscountRulePanel
          rule={selectedDiscount}
          brands={brandOptions}
          onClose={() => { setSelectedDiscount(null); setCreatingRule(false); }}
          onSaved={(saved, keepCreating) => {
            // Reload rather than splice the saved row in. The list is small
            // and bounded, and a local splice would diverge from the table the
            // moment the backend normalised anything -- an archived rule
            // changing status, or a percentage stored to a different
            // precision than what was typed.
            //
            // ⚠️ `keepCreating` LEAVES THIS PANEL IN CREATE MODE. The panel has
            // already reseeded its own fields for the next rule; selecting the
            // row that was just saved would change `rule` from null to an
            // object, fire the reseed effect, and overwrite that fresh form
            // with the rule the Admin has just finished with. The saved row
            // still lands in the table below via loadDiscounts, which is where
            // it belongs.
            setDiscountSavedText(
              `Saved: ${saved.partnerName ?? "customer"} · ${saved.brandName ?? "brand"} ${saved.discountPct}%.`,
            );
            if (keepCreating) {
              setCreatingRule(true);
              setSelectedDiscount(null);
            } else {
              // app review P2-5: Save closes the panel; the row is in the table.
              setCreatingRule(false);
              setSelectedDiscount(null);
            }
            void loadDiscounts();
          }}
        />
      )}
    </div>
  );
}

function ReferenceNotice({ testid }: { testid: string }) {
  return (
    <div
      data-testid={testid}
      className="flex items-start"
      style={{
        padding: spacing[12], marginBottom: spacing[12],
        backgroundColor: semanticColors.panelBg,
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
      }}
    >
      <Info
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        color={semanticColors.textMuted}
        style={{ flexShrink: 0, marginTop: spacing[2] }}
      />
      <span>{BRAND_LOCK_NOTICE}</span>
    </div>
  );
}

function BrandLocksTab({
  rules, totalCount, categoryOptions,
  search, onSearch,
  category, onCategory,
  status, onStatus,
  selectedId, onRowClick,
}: {
  rules: BrandLockRule[];
  totalCount: number;
  categoryOptions: string[];
  search: string; onSearch: (v: string) => void;
  category: string; onCategory: (v: string) => void;
  status: string; onStatus: (v: string) => void;
  selectedId?: string;
  onRowClick: (r: BrandLockRule) => void;
}) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <ReferenceNotice testid="brand-locks-reference-notice" />
      {totalCount === 0 ? (
        <div
          data-testid="brand-locks-unavailable"
          style={{
            padding: spacing[40], textAlign: "center",
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
          }}
        >
          Brand-lock management is unavailable until an authoritative Odoo source is configured.
        </div>
      ) : (
        <>
      <div
        data-testid="rules-filter-bar"
        className="flex items-center"
        style={{
          height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
          backgroundColor: semanticColors.panelBg,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          gap: spacing[12],
        }}
      >
        <div
          className="flex items-center"
          style={{
            flex: 1, minWidth: layout.searchMinWidth,
            height: heights.input, padding: `0 ${spacing[8]}px`,
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            gap: spacing[6],
          }}
        >
          <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
          <input
            data-testid="rules-search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search client"
            style={{
              flex: 1, minWidth: 0, height: "100%",
              fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
              background: "transparent", border: "none", outline: "none",
            }}
          />
        </div>
        {categoryOptions.length > 0 && (
          <select data-testid="rules-filter-category" value={category}
                  onChange={(e) => onCategory(e.target.value)} style={SELECT_STYLE}>
            <option value="All">Category: All</option>
            {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select data-testid="rules-filter-status" value={status}
                onChange={(e) => onStatus(e.target.value)} style={SELECT_STYLE}>
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === "All" ? "Status: All" : s}</option>)}
        </select>
        {/* THE "Add Rule" BUTTON WAS HERE AND IS GONE. An indigo primary with
            no onClick: pressing it did nothing and reported nothing, which is
            the worst outcome available because it looks like success. There
            is no model to create a rule in -- D3 builds one -- and the notice
            above this bar is where that fact belongs. */}
      </div>

      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
        <span data-testid="rules-filtered-count" className="tabular-nums">{rules.length}</span>
        {rules.length !== totalCount ? (
          <> of <span className="tabular-nums">{totalCount}</span> rules</>
        ) : (
          <> rules</>
        )}
      </div>

      <BrandLockTable rows={rules} onRowClick={onRowClick} selectedId={selectedId} />
        </>
      )}
    </div>
  );
}

const DISCOUNT_STATUS_OPTS = ["All", "Active", "Archived"];

function DiscountsTab({
  rows, totalCount, loadStatus, loadError, onRetry, canEdit, onNew,
  oemOptions,
  search, onSearch,
  oem, onOem,
  status, onStatus,
  selectedId, onRowClick,
}: {
  rows: OemDiscountRule[];
  totalCount: number;
  loadStatus: LoadStatus;
  loadError: string;
  onRetry: () => void;
  canEdit: boolean;
  onNew: () => void;
  oemOptions: string[];
  search: string; onSearch: (v: string) => void;
  oem: string; onOem: (v: string) => void;
  status: string; onStatus: (v: string) => void;
  selectedId?: string;
  onRowClick: (r: OemDiscountRule) => void;
}) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* ⚠️ NO ReferenceNotice ON THIS TAB ANY MORE. It said these rules ship
          with the build and cannot be saved. They now come out of
          anixify.discount.rule and an Admin can write them, so repeating it
          would be false. The brand-lock tab keeps its notice, because brand
          locks still have no backend. */}
      {/* Info line */}
      <div
        data-testid="discounts-info-bar"
        className="flex items-center"
        style={{
          minHeight: heights.control, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[12], flexShrink: 0,
          backgroundColor: colors.indigo50,
          border: `1px solid ${colors.indigo100}`, borderRadius: radius.base,
          gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
        }}
      >
        <Info size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600} />
        {/* ⚠️ THIS SENTENCE DESCRIBES THE MODEL, AND IT HAS BEEN WRONG TWICE.
            It first promised "Project-level rules beat client-level rules",
            which nothing implemented. U2 corrected it to describe
            findDiscountPct's mock matching. D3a-i replaced the model itself,
            so it changes again -- and the important half is now the SECOND
            sentence, because it is what stops somebody recording a
            competitive bid here.

            ⚠️ NO PRECEDENCE IS DESCRIBED BECAUSE THERE IS NONE. One active
            rule per customer and OEM is enforced by a unique index; a second
            is refused rather than ranked. That was the owner's decision --
            a precedence rule silently picks a winner, a constraint makes
            somebody look. */}
        <span>
          One standing rate per customer and OEM. A second active rule for the
          same pairing is refused, not ranked — archive the old one to
          supersede it. A competitive bid is not a rule: enter the net cost the
          OEM quoted on the quote line instead.
        </span>
      </div>

      {loadStatus === "error" && (
        <ReferenceError
          testId="discounts-load-error"
          retryTestId="discounts-retry"
          message="Could not load the discount rules."
          detail={loadError}
          onRetry={onRetry}
        />
      )}

      {loadStatus === "loading" && (
        <ReferenceLoading testId="discounts-loading" label="Loading discount rules…" />
      )}

      {/* ⚠️ THE EMPTY STATE CARRIES THE CREATE ACTION, because this is the
          state an Admin is in before the first rule exists. Its label has
          always said "Add one for a customer and OEM" -- an instruction with
          no control to carry it out, since the only "Add Discount" button sat
          in the filter bar below, which renders on totalCount > 0. The first
          rule was therefore unreachable through the UI.

          Non-Admins see the label naming who maintains the rules and no
          button, exactly as in the filter bar: save_discount_rule is
          Admin-only on the backend, and offering it to Sales offers a 403. */}
      {loadStatus === "ok" && totalCount === 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: spacing[12] }}>
          <div style={{ alignSelf: "stretch" }}>
            <ReferenceEmpty
              testId="discounts-empty"
              label={canEdit
                ? "No discount rules yet. Add one for a customer and OEM."
                : "No discount rules yet. An Anixify Admin maintains them."}
            />
          </div>
          {/* ⚠️ ITS OWN TESTID, NOT THE FILTER BAR'S. Two reasons, and the
              second is why it is not a style choice. It is a different
              affordance -- this one creates the FIRST rule, from the state
              where no other control exists -- and, because the two sites would
              otherwise be textually identical, reusing the id would leave this
              slice with NO string literal unique to it. deploy/check-bundle.sh
              can then not tell a build carrying the empty-state site from one
              without it, which is exactly the silent revert the manifest
              exists to catch. See the row in deploy/bundle-signatures.tsv. */}
          {canEdit && (
            <button
              data-testid="discounts-add-first-discount"
              className="btn-primary"
              onClick={onNew}
              style={ADD_DISCOUNT_BUTTON_STYLE}
            >
              Add Discount
            </button>
          )}
        </div>
      )}

      {/* ⚠️ FAIL LOUD: the filter bar and the table render only on a
          successful, non-empty read. A filter bar over an empty grid reads as
          "nothing matched" rather than "we could not ask". */}
      {loadStatus === "ok" && totalCount > 0 && (
      <>
      {/* Filter bar */}
      <div
        data-testid="discounts-filter-bar"
        className="flex items-center"
        style={{
          height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
          backgroundColor: semanticColors.panelBg,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          gap: spacing[12],
        }}
      >
        <div
          className="flex items-center"
          style={{
            flex: 1, minWidth: layout.searchMinWidth,
            height: heights.input, padding: `0 ${spacing[8]}px`,
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            gap: spacing[6],
          }}
        >
          <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
          <input
            data-testid="discounts-search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search client or OEM"
            style={{
              flex: 1, minWidth: 0, height: "100%",
              fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
              background: "transparent", border: "none", outline: "none",
            }}
          />
        </div>
        {oemOptions.length > 0 && (
          <select data-testid="discounts-filter-oem" value={oem}
                  onChange={(e) => onOem(e.target.value)} style={SELECT_STYLE}>
            <option value="All">OEM: All</option>
            {oemOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {/* ⚠️ CATEGORY AND DEAL TYPE FILTERS ARE GONE WITH THEIR COLUMNS.
            The model has neither field: category is not a match key (owner's
            decision), and deal type lives on the LINE because the rule table
            holds standing rates only. A filter for a field that does not exist
            would always match everything. */}
        {/* Status stays a literal: it is the range of statusOf, a verdict
            computed from `active`, not a column any row holds. Deriving it
            would drop "Archived" on a day when nothing happened to be
            archived -- which is when somebody looks. */}
        <select data-testid="discounts-filter-status" value={status}
                onChange={(e) => onStatus(e.target.value)} style={SELECT_STYLE}>
          {DISCOUNT_STATUS_OPTS.map((s) => (
            <option key={s} value={s}>{s === "All" ? "Status: All" : s}</option>
          ))}
        </select>
        {/* ⚠️ THE "Add Discount" BUTTON IS BACK, AND THIS TIME IT WORKS. U2
            removed it because it was an indigo primary with no onClick. It
            returns only for an Admin, because save_discount_rule is Admin-only
            on the backend and offering it to Sales would be offering a 403. */}
        {canEdit && (
          <button
            data-testid="discounts-add-discount"
            className="btn-primary"
            onClick={onNew}
            style={ADD_DISCOUNT_BUTTON_STYLE}
          >
            Add Discount
          </button>
        )}
      </div>

      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
        <span data-testid="discounts-filtered-count" className="tabular-nums">{rows.length}</span>
        {rows.length !== totalCount ? (
          <> of <span className="tabular-nums">{totalCount}</span> discounts</>
        ) : (
          <> discounts</>
        )}
      </div>

      <OemDiscountsTable rows={rows} onRowClick={onRowClick} selectedId={selectedId} />
      </>
      )}
    </div>
  );
}
